import _ from 'lodash';
import classNames from 'classnames';
import sanitizeHtml from 'sanitize-html';
import React, { Component, PropTypes } from 'react';
import { Row, Col, Modal} from 'react-bootstrap';
import Timestamp from 'components/Timestamp';
import ComponentTitle from 'components/shared/ComponentTitle';
import SocialIcon from 'components/social/SocialIcon';

/**
 * Class representing the meteorologists written forecast for the current day.
 *
 * Dependent on feed - station_weather_feed
 *
 */
class SimpleMeteorologistForecast extends Component {
  static propTypes = {
    FRN_rawResponses: PropTypes.array,
    showOnUserLocation: PropTypes.bool,
    showForecast: PropTypes.bool,
    showMeteorologistImage: PropTypes.bool,
    showMeteorologistInfo: PropTypes.bool,
    backgroundColor: PropTypes.string,
    backgroundUri: PropTypes.string,
    textColor: PropTypes.string,
    capitalizeForecastTitle: PropTypes.bool,
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    })
  }

  static contextTypes = {
    location: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired
  }

  defaultProps = {
    showForecast: false,
    showMeteorologistImage: false,
    showMeteorologistInfo: false,
    backgroundColor: '#FFF',
    textColor: '#000000',
    showOnUserLocation: false,
    capitalizeForecastTitle: true
  }

  state = {
    showModal: false
  }

  _setModalVisible() {
    this.setState({
      showModal: true
    });
  }

  _setModalHidden() {
    this.setState({
      showModal: false
    });
  }

  render() {
    const {
      showForecast,
      showMeteorologistImage,
      showMeteorologistInfo,
      backgroundUri,
      backgroundColor = '#FFFFFF',
      textColor = '#000000',
      showOnUserLocation,
      timestampOptions = {},
      capitalizeForecastTitle,
      FRN_rawResponses: [
        { data:
          { features:
            forecastData = []
            } = {}
          } = {}
        ] = []
      } = this.props;

    if (!showForecast && !showMeteorologistImage && !showMeteorologistInfo) {
      return null;
    }

    const { showModal } = this.state;
    const { config, location } = this.context;
    const { affiliate: { locations: stationLocations = [] } = {} } = config;
    const weatherData = _.find(forecastData, {type: 'weather'}) || {};
    const {
      'header': forecastTitle = 'Today\'s Forecast',
      'currentconditions': forecastDescription = '',
      'lastupdatedateutc': pubDate = '',
      'byline': bylineInfo = {} } = weatherData;
    const name = _.get(bylineInfo, 'firstname', "") + ' ' + _.get(bylineInfo, 'lastname', '');
    const meteorologistTitle = _.get(bylineInfo, 'title', '');
    const meteorologistImage = _.get(bylineInfo, 'profileimage', '');
    const email = _.get(bylineInfo, 'emailaddress', '');
    const { socialnetworksinfo: {
      facebook: facebookHandle = '',
      twitter: twitterHandle = '',
      google: googlePlusId = ''} = {} } = bylineInfo;
    const facebookInfo = facebookHandle ? 'https://www.facebook.com/' + facebookHandle : '';
    const twitterInfo = twitterHandle ? 'https://twitter.com/' + twitterHandle : '';
    const googleInfo = googlePlusId ? 'https://plus.google.com/' + googlePlusId : '';
    // We reinstate escaped characters removed in the parsing process, and strip out any custom styles added in producer
    const unescapedSanitizedDescription = sanitizeHtml(_.unescape(forecastDescription));
    const { showElapsedTime, displayShortDateTime } = timestampOptions;

    let customComponentStyles = {};

    // If the current location isn't a default location in the config, then hide the meteorologist report since it's not
    // applicable for the selected location. This can be overriden by setting showOnUserLocation to true in the config
    if (!showOnUserLocation && !_.find(stationLocations, { 'zipcode': Number(location.zipcode)})){
      return null;
    }

    customComponentStyles['color'] = textColor;
    customComponentStyles['backgroundColor'] = backgroundColor;

    if (backgroundUri) {
      customComponentStyles['backgroundImage'] = 'url(' + backgroundUri + ')';
    }

    const textColSpans = {
      md: 6,
      sm: 9,
      xs: 12
    };
    const profileColSpans = {
      md: 6,
      sm: 3,
      xs: 12
    };

    if (!showMeteorologistImage && !showMeteorologistInfo) {
      textColSpans['md'] = 12;
      textColSpans['sm'] = 12;
    }

    if (!showForecast) {
      profileColSpans['md'] = 12;
      profileColSpans['sm'] = 12;
    }

    const forecastTitleClassnames = classNames(
      'SimpleMeteorologistForecast-forecastTitle',
      { 'SimpleMeteorologistForecast-forecastTitle--capitalize': capitalizeForecastTitle }
    );

    return (
      <div className="SimpleMeteorologistForecast" style={customComponentStyles}>
        <Row>
          { showForecast && <Col {...textColSpans}>
            { forecastData.length ?
              <div className="SimpleMeteorologistForecast-textCell">
                <div className="SimpleMeteorologistForecast-textContainer">
                  <h4 className={forecastTitleClassnames}>{forecastTitle}</h4>
                  <Timestamp publishDate={pubDate} showElapsedTime={showElapsedTime} displayShortDateTime={displayShortDateTime}/>
                  <div className="SimpleMeteorologistForecast-forecastDetails" onClick={() => this._setModalVisible() }>
                    <div className="SimpleMeteorologistForecast-forecastTextContainer">
                      <ul className="SimpleMeteorologistForecast-forecastText">
                        <li
                          dangerouslySetInnerHTML={{__html: unescapedSanitizedDescription }}
                        />
                      </ul>
                    </div>
                    <div className="readMore">Read more</div>
                  </div>
                </div>
              </div> : null }
          </Col> }
          { (showMeteorologistImage || showMeteorologistInfo) && <Col {...profileColSpans}>
            <div className="SimpleMeteorologistForecast-profileCell">
              { (showMeteorologistImage && meteorologistImage) &&
              <div className="SimpleMeteorologistForecast-profileImage">
                <img src={meteorologistImage} alt={'Image of ' + (name || 'meteorologist')} />
              </div> }
              { showMeteorologistInfo && <div className="SimpleMeteorologistForecast-profileInfo">
                <div className="SimpleMeteorologistForecast-name">
                  {name || ''}
                </div>
                <div className="SimpleMeteorologistForecast-meteorologistTitle">
                  {meteorologistTitle || ''}
                </div>
                <div className="SimpleMeteorologistForecast-socialLinks">
                  {facebookInfo ? <SocialIcon iconType="facebook" destinationUri={facebookInfo} /> : null}
                  {twitterInfo ? <SocialIcon iconType="twitter" destinationUri={twitterInfo} /> : null}
                  {googleInfo ? <SocialIcon iconType="google" destinationUri={googleInfo} /> : null}
                  {email ? <SocialIcon iconType="email" destinationUri={'mailto:' + email} noTarget /> : null}
                </div>
              </div> }
            </div>
          </Col> }
          {backgroundUri ? <div className="SimpleMeteorologistForecast-backgroundOverlay" /> : null}
        </Row>
        <MeteorologistForecastModal
          title={forecastTitle}
          forecastContent={unescapedSanitizedDescription}
          meteorlogistName={name}
          meteorologistTitle={meteorologistTitle}
          meteorologistImage={meteorologistImage}
          email={email}
          facebookInfo={facebookInfo}
          twitterInfo={twitterInfo}
          googleInfo={googleInfo}
          forecastDate={pubDate}
          showModal={showModal}
          timestampOptions={timestampOptions}
          onHideModal={this._setModalHidden.bind(this)} />
      </div>
    );
  }
}

/**
 * Class representing the modal for the full meteorologist text
 */
class MeteorologistForecastModal extends Component {
  static propTypes = {
    title: PropTypes.string,
    forecastContent: PropTypes.string.isRequired,
    meteorlogistName: PropTypes.string,
    meteorologistTitle: PropTypes.string,
    meteorologistImage: PropTypes.string,
    email: PropTypes.string,
    facebookInfo: PropTypes.string,
    twitterInfo: PropTypes.string,
    googleInfo: PropTypes.string,
    forecastDate: PropTypes.string,
    showModal: PropTypes.bool.isRequired,
    onHideModal: PropTypes.func.isRequired,
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    })
  }

  render() {
    const {
      title = '',
      forecastContent = '',
      meteorlogistName = '',
      meteorologistTitle = '',
      meteorologistImage = '',
      email = '',
      facebookInfo = '',
      twitterInfo = '',
      googleInfo = '',
      forecastDate = '',
      showModal,
      onHideModal,
      timestampOptions: {
        showElapsedTime,
        displayShortDateTime
      } = {}
    } = this.props;

    return (
      <Modal className="SimpleMeteorologistForecastModal"
             show={showModal}
             onHide={onHideModal}
             backdrop
             aria-labelledby="contained-modal-title-lg">
        <Modal.Header className="SimpleMeteorologistForecastModal-header" closeButton />
        <Modal.Body>
          { title ? <ComponentTitle title={title} classes="SimpleMeteorologistForecastModal-title" /> : null }
          <Timestamp publishDate={forecastDate} showElapsedTime={showElapsedTime} displayShortDateTime={displayShortDateTime}/>
          <div className="SimpleMeteorologistForecastModal-profileCell">
            <div className="SimpleMeteorologistForecastModal-profileImage">
              <img src={meteorologistImage} alt={'Image of ' + (meteorlogistName || 'meteorologist')} />
            </div>
            <div className="SimpleMeteorologistForecastModal-profileInfo">
              <div className="SimpleMeteorologistForecastModal-name">
                {meteorlogistName}
              </div>
              <div className="SimpleMeteorologistForecastModal-meteorologistTitle">
                {meteorologistTitle}
              </div>
              <div className="SimpleMeteorologistForecastModal-socialLinks">
                {facebookInfo ? <SocialIcon iconType="facebook" destinationUri={facebookInfo} /> : null}
                {twitterInfo ? <SocialIcon iconType="twitter" destinationUri={twitterInfo} /> : null}
                {googleInfo ? <SocialIcon iconType="google" destinationUri={googleInfo} /> : null}
                {email ? <SocialIcon iconType="email" destinationUri={'mailto:' + email} noTarget /> : null}
              </div>
            </div>
          </div>
          <div className="SimpleMeteorologistForecastModal-forecastContent" dangerouslySetInnerHTML={{__html: forecastContent }} />
        </Modal.Body>
      </Modal>
    );
  }
}

export default SimpleMeteorologistForecast;



// WEBPACK FOOTER //
// ./app/components/custom/heartland/weather/SimpleMeteorologistForecast.js